import React, { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Order } from '../../../common/model/dto/order/order';
import { OrderDataUtils } from '../../../common/utils/services/order-data-utils';
import {
  getFulfillmentStatusTone,
  getOrderStatusTone,
  getPaymentStatusTone,
  isFailedOrder,
} from '../../../common/utils/services/order-status-utils';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import useRowNavigation from '../../../hooks/use-row-navigation';
import OvLabelledField from '../atoms/OvLabelledField';
import OvPillLink from '../atoms/OvPillLink';
import OvStatusChip from '../atoms/OvStatusChip';
import OvOrderDeliveryStatus from '../molecules/OvOrderDeliveryStatus';
import OvOrderDiscount from '../molecules/OvOrderDiscount';
import OvOrderIdentity from '../molecules/OvOrderIdentity';

interface OvOrderListCardProps {
  order: Order;
  detailsPath: string;
}

const OvOrderListCard: FC<OvOrderListCardProps> = ({ order, detailsPath }) => {
  const { t } = useTranslation();
  const onCardClick = useRowNavigation(detailsPath);

  return (
    <Card $isFailed={isFailedOrder(order.status)} onClick={onCardClick}>
      <Top>
        <Identity>
          <OvOrderIdentity order={order} detailsPath={detailsPath} />
        </Identity>
        <Fields>
          <CustomerField label={t('orders.tableHeaders.customer')}>
            {OrderDataUtils.getCustomerShortName(order)}
            <Secondary>
              {OrderDataUtils.formatDateTime(
                order.ordered_at,
                'DD MMM YYYY · HH:mm'
              )}
            </Secondary>
          </CustomerField>
          <TotalField label={t('orders.tableHeaders.total')}>
            <strong>
              {OrderDataUtils.formatAmount(OrderDataUtils.getTotal(order))}
            </strong>
            <Secondary>
              {t('orders.itemCount', {
                count: OrderDataUtils.getItemCount(order),
              })}
            </Secondary>
          </TotalField>
          <DiscountField label={t('orders.tableHeaders.discount')}>
            <OvOrderDiscount order={order} />
          </DiscountField>
        </Fields>
        <DetailsLink
          to={detailsPath}
          aria-label={t('orders.detailsFor', { id: order.internal_id })}
        >
          {t('orders.orderDetailsCta')}
        </DetailsLink>
      </Top>
      <StatusRail>
        <OvLabelledField label={t('orders.tableHeaders.order')}>
          <OvStatusChip
            label={order.status}
            tone={getOrderStatusTone(order.status)}
          />
        </OvLabelledField>
        <OvLabelledField label={t('orders.tableHeaders.paymentStatus')}>
          <OvStatusChip
            label={order.payment_status}
            tone={getPaymentStatusTone(order.payment_status)}
          />
        </OvLabelledField>
        <OvLabelledField label={t('orders.tableHeaders.fulfillmentStatus')}>
          <OvStatusChip
            label={order.fulfillment_status}
            tone={getFulfillmentStatusTone(order.fulfillment_status)}
          />
        </OvLabelledField>
        <OvLabelledField label={t('orders.tableHeaders.delivery')}>
          <OvOrderDeliveryStatus order={order} showDetail />
        </OvLabelledField>
      </StatusRail>
    </Card>
  );
};

export default OvOrderListCard;

const Card = styled.li<{ $isFailed: boolean }>`
  padding: 1rem 0.75rem;
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  cursor: pointer;
  list-style: none;

  ${({ $isFailed }) =>
    $isFailed && `box-shadow: inset 3px 0 0 ${Colours.OV_RED};`}

  &:hover {
    background-color: ${Colours.OV_WHITE_HOVER};
  }

  &:last-child {
    border-bottom: 0;
  }
`;

const Top = styled.div`
  display: grid;
  grid-template-columns: 1.45fr 1.2fr 0.7fr 1fr auto;
  grid-template-areas: 'identity customer total discount link';
  gap: 1.25rem;
  align-items: start;

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1.5fr 1fr auto;
    grid-template-areas:
      'identity total link'
      'customer discount discount';
    gap: 0.75rem;
  }

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      'identity link'
      'customer total'
      'discount discount';
  }
`;

const Identity = styled.div`
  grid-area: identity;
  min-width: 0;
  font-weight: bold;
`;

const Fields = styled.dl`
  display: contents;
`;

const CustomerField = styled(OvLabelledField)`
  grid-area: customer;
`;

const TotalField = styled(OvLabelledField)`
  grid-area: total;
`;

const DiscountField = styled(OvLabelledField)`
  grid-area: discount;
`;

const Secondary = styled.span`
  display: block;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;

const DetailsLink = styled(OvPillLink)`
  grid-area: link;
  justify-self: end;
`;

const StatusRail = styled.dl`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1.5fr;
  gap: 0.75rem 1.25rem;
  margin: 0.75rem 0 0;
  padding-top: 0.75rem;
  border-top: 1px solid rgb(235, 237, 240);

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr 1fr;
  }
`;
